// FacilityForm — add or edit a hospital, practice, office or referrer. The
// address lives here; physicians at the facility inherit it. An NPI (type 2,
// the organisation's) is optional — most small practices bill under the
// physician's — and can be found in NPPES by name.
import { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { saveFacility, listFacilities, nppesSearchOrg, isValidNpi, fmtPhone, FACILITY_TYPES, CONTACT_ROUTES } from '../../lib/directory';
import { Modal, ErrorBox, label, field, section, RefCode } from './ui';

const blank = (seed = {}) => ({
  name: '', facility_type: 'pediatric_practice', parent_id: '', npi: '', address_line1: '', address_line2: '', city: '', state: 'GA', zip: '',
  phone: '', fax: '', records_fax: '', email: '', website: '', preferred_contact: '', notes: '', status: 'active', ...seed,
});

export default function FacilityForm({ facility, initialName, facilities: given, onSaved, onClose, onVerify }) {
  const { profile } = useAuth();
  const actor = profile?.full_name || 'staff';
  const [form, setForm] = useState(() => blank(facility || { name: initialName || '' }));
  const [all, setAll] = useState(given || []);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [candidates, setCandidates] = useState([]);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  useEffect(() => { if (!given) listFacilities().then(({ data }) => setAll(data || [])); }, [given]);
  const parent = form.parent_id ? all.find(f => f.id === form.parent_id) : null;

  const searchOrg = async () => {
    if (!form.name.trim()) return;
    setBusy('org'); setErr(''); setCandidates([]);
    try {
      const ms = await nppesSearchOrg(form.name.trim(), form.state || 'GA');
      if (!ms.length) setErr(`The registry has no organisation named “${form.name}” in ${form.state || 'GA'}. Small practices often bill under the doctor's own NPI — that's fine, leave it blank.`);
      else setCandidates(ms);
    } catch (e) { setErr(e.message); }
    setBusy('');
  };
  const applyOrg = (m) => {
    setForm(f => ({ ...f, npi: m.npi,
      address_line1: f.address_line1 || m.address_line1 || '', address_line2: f.address_line2 || m.address_line2 || '',
      city: f.city || m.city || '', state: f.state || m.state || 'GA', zip: f.zip || m.zip || '',
      phone: f.phone || m.phone || '', fax: f.fax || m.fax || '' }));
    setCandidates([]);
  };

  const save = async () => {
    setBusy('save'); setErr('');
    const { data, error } = await saveFacility({ ...form, id: facility?.id, parent_id: form.parent_id || null }, actor);
    setBusy('');
    if (error) { setErr(error.message); return; }
    onSaved(data);
  };
  const grid = (cols) => ({ display: 'grid', gridTemplateColumns: cols, gap: 12 });

  return (
    <Modal onClose={onClose} width={720}
      title={<>{facility ? <>Edit <RefCode code={facility.ref_code} /> {facility.name}</> : 'Add facility'}</>}
      footer={<>
        {facility && onVerify && <button className="btn btn-outline" style={{ marginRight: 'auto', color: 'var(--success)', borderColor: 'var(--success-border)' }} onClick={() => onVerify(facility)}>✓ Details confirmed today</button>}
        <button className="btn btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={busy === 'save'}>{busy === 'save' ? 'Saving…' : (facility ? 'Save changes' : 'Add to directory')}</button>
      </>}>
      <ErrorBox>{err}</ErrorBox>
      <div style={section}>Identity</div>
      <div style={grid('2fr 1fr')}>
        <div><div style={label}>Name</div><input style={field} value={form.name} onChange={set('name')} placeholder="Rainbow Pediatric Clinic" /></div>
        <div>
          <div style={label}>Type</div>
          <select style={field} value={form.facility_type} onChange={set('facility_type')}>
            {FACILITY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
      </div>
      <div style={{ ...grid('1fr 1fr auto'), marginTop: 12 }}>
        <div>
          <div style={label}>Part of</div>
          <select style={field} value={form.parent_id || ''} onChange={set('parent_id')}>
            <option value="">— standalone —</option>
            {all.filter(f => f.id !== facility?.id && !f.parent_id).map(f => <option key={f.id} value={f.id}>{f.ref_code} · {f.name}</option>)}
          </select>
        </div>
        <div>
          <div style={label}>Organisation NPI <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></div>
          <input style={{ ...field, fontFamily: 'var(--mono)' }} value={form.npi ?? ''} onChange={set('npi')} inputMode="numeric"
            onBlur={() => { if (form.npi && !isValidNpi(form.npi)) setErr('That NPI fails the check digit — please re-check it.'); }} />
        </div>
        <div style={{ alignSelf: 'end' }}><button className="btn btn-outline" onClick={searchOrg} disabled={busy === 'org' || !form.name}>{busy === 'org' ? 'Searching…' : 'Find in NPPES'}</button></div>
      </div>
      {candidates.length > 0 && (
        <div style={{ marginTop: 12, border: '1px solid var(--accent-border)', borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ padding: '8px 12px', background: 'var(--accent-bg)', fontSize: 12.5, color: 'var(--accent)', fontWeight: 600 }}>{candidates.length} organisation{candidates.length === 1 ? '' : 's'} in the registry</div>
          {candidates.map(m => (
            <div key={m.npi} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderTop: '1px solid var(--border)', fontSize: 13 }}>
              <div style={{ flex: 1 }}><b>{m.last_name}</b><div style={{ fontSize: 12, color: 'var(--text3)' }}>NPI {m.npi} · {[m.address_line1, m.city, m.state, m.zip].filter(Boolean).join(', ')}{m.fax ? ` · fax ${fmtPhone(m.fax)}` : ''}</div></div>
              <button className="btn btn-outline btn-sm" onClick={() => applyOrg(m)}>This one</button>
            </div>
          ))}
        </div>
      )}

      <div style={section}>Address</div>
      <div style={grid('1fr')}><div><div style={label}>Street</div><input style={field} value={form.address_line1 || ''} onChange={set('address_line1')} /></div></div>
      <div style={{ ...grid('1fr'), marginTop: 12 }}><div><div style={label}>Suite / floor</div><input style={field} value={form.address_line2 || ''} onChange={set('address_line2')} /></div></div>
      <div style={{ ...grid('2fr 80px 120px'), marginTop: 12 }}>
        <div><div style={label}>City</div><input style={field} value={form.city || ''} onChange={set('city')} /></div>
        <div><div style={label}>State</div><input style={field} value={form.state || ''} onChange={set('state')} maxLength={2} /></div>
        <div><div style={label}>ZIP</div><input style={field} value={form.zip || ''} onChange={set('zip')} inputMode="numeric" /></div>
      </div>

      <div style={section}>Contact</div>
      <div style={grid('1fr 1fr 1fr')}>
        <div><div style={label}>Main phone</div><input style={field} value={form.phone || ''} onChange={set('phone')} inputMode="tel" /></div>
        <div><div style={label}>Fax</div><input style={field} value={form.fax || ''} onChange={set('fax')} inputMode="tel"
          placeholder={parent?.fax ? `↑ ${fmtPhone(parent.fax)}` : ''} title={parent?.fax ? `Blank uses ${parent.name}'s fax` : undefined} /></div>
        <div><div style={label}>Email</div><input style={field} value={form.email || ''} onChange={set('email')} inputMode="email" /></div>
      </div>
      <div style={{ ...grid('1fr 2fr'), marginTop: 12 }}>
        <div><div style={label}>Records fax</div><input style={field} value={form.records_fax || ''} onChange={set('records_fax')} inputMode="tel"
          placeholder={parent?.records_fax ? `↑ ${fmtPhone(parent.records_fax)}` : ''} /></div>
        <div style={{ fontSize: 11.5, color: 'var(--text3)', alignSelf: 'end', paddingBottom: 6 }}>
          Where requests for medical records go, if not the fax above — a hospital system's records office.
          {parent ? ` Left blank, this site uses ${parent.name}'s fax and records fax.` : ' Sites under this one use it too.'}
        </div>
      </div>
      <div style={{ ...grid('180px 1fr'), marginTop: 12 }}>
        <div>
          <div style={label}>Best route</div>
          <select style={field} value={form.preferred_contact || ''} onChange={set('preferred_contact')}>{CONTACT_ROUTES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}</select>
        </div>
        <div><div style={label}>Website</div><input style={field} value={form.website || ''} onChange={set('website')} /></div>
      </div>
      <div style={{ marginTop: 12 }}><div style={label}>Notes — who to call, hours, quirks</div><textarea style={{ ...field, minHeight: 60, resize: 'vertical' }} value={form.notes || ''} onChange={set('notes')} /></div>
      {facility && (
        <div style={{ ...grid('180px 1fr'), marginTop: 12 }}>
          <div><div style={label}>Status</div>
            <select style={field} value={form.status} onChange={set('status')}><option value="active">Active</option><option value="inactive">Inactive (closed)</option></select>
          </div>
        </div>
      )}
    </Modal>
  );
}
